// Health tracker — per-spec (iter-25):
//   Look at every task whose status is NOT 'Done'.
//   For each, find the most relevant "due" date — the first date column whose
//   label contains 'due' (case-insensitive), or fall back to the project's
//   go_live_date if the task carries one. Compute days since the due date.
//
//     overdue >= 20 days        → OFF TRACK  (red)
//     overdue 10..19 days       → AT RISK    (yellow)
//     otherwise                 → HEALTHY    (green)
//
// Project stoplight = the worst tier across open tasks. If 1 task is RED, the
// whole project lights up RED.
//
// Returns:
//   {
//     state: 'green' | 'yellow' | 'red',
//     label: 'Healthy' | 'At risk' | 'Off track',
//     counts: { green, yellow, red, totalOpen, done, total },
//     worst: <task or null>,
//   }

import { getStatuses, isDoneStatus } from "@/lib/gantt-utils";

export const HEALTH_LABEL = {
  green: "Healthy",
  yellow: "At risk",
  red: "Off track",
};

export const HEALTH_DOT = {
  green: "#10b981",
  yellow: "#f59e0b",
  red: "#ef4444",
};

export const HEALTH_TONE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  yellow: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
};

function pickDueDate(task, project) {
  const cols = project?.date_columns || [];
  // 1) any date column labeled "due" / "due date" / "deadline"
  const dueCol = cols.find((c) => /due|deadline/i.test(c.label || ""));
  if (dueCol && task.dates?.[dueCol.id]) return task.dates[dueCol.id];
  // 2) fall back to go_live_date for top-level tasks
  if (!task.parent_id && project?.go_live_date) return project.go_live_date;
  return null;
}

export function computeTaskHealth(task, project, today = new Date()) {
  if (isDoneStatus(getStatuses(project), task.status)) return { state: "green", overdueDays: 0 };
  const due = pickDueDate(task, project);
  if (!due) return { state: "green", overdueDays: 0 };
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return { state: "green", overdueDays: 0 };
  const diff = Math.floor((today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (diff >= 20) return { state: "red", overdueDays: diff };
  if (diff >= 10) return { state: "yellow", overdueDays: diff };
  return { state: "green", overdueDays: Math.max(diff, 0) };
}

export function computeProjectHealth(tasks, project) {
  const today = new Date();
  const counts = { green: 0, yellow: 0, red: 0, totalOpen: 0, done: 0, total: tasks.length };
  let worst = null;
  let worstState = "green";
  const rank = { green: 0, yellow: 1, red: 2 };
  const statuses = getStatuses(project);
  for (const t of tasks) {
    if (isDoneStatus(statuses, t.status)) {
      counts.done += 1;
      continue;
    }
    counts.totalOpen += 1;
    const h = computeTaskHealth(t, project, new Date(today));
    counts[h.state] += 1;
    if (rank[h.state] > rank[worstState]) {
      worstState = h.state;
      worst = t;
    }
  }
  return { state: worstState, label: HEALTH_LABEL[worstState], counts, worst };
}
